import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import type { PageType } from './Header';
import {
  Building2,
  Package,
  Wrench,
  Shield,
  Layers,
  Factory,
  CheckCircle2,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

type InfrastructurePageProps = {
  onBack?: () => void;
  onNavigate: (page: PageType) => void;
  /** When true, render only the content sections (no hero, no back). Used on combined Products page. */
  embedMode?: boolean;
};

const keyFacts = [
  { value: '5,430 sq.m', label: 'Covered Manufacturing Space', icon: Building2 },
  { value: '7', label: 'Independent Bays (Largest 60m × 22m × 13.5m)', icon: Factory },
  { value: '5–250 Tonne', label: 'Component Handling', icon: Package },
  { value: 'ISO 9001 | 14001 | 45001 | 3834 | EN1090', label: 'Certified (25+ years)', icon: Shield },
  { value: 'Defence', label: 'Quality-Approved Welders', icon: Wrench },
  { value: 'Since 2001', label: 'Exports to Malaysia, Australia, UAE, USA, Europe', icon: Layers },
];

const capabilityCards = [
  {
    title: 'Industrial Engineering Design',
    description: 'Comprehensive engineering design services from concept to production using 3D CAD, Finite Element Analysis (FEA) to client specifications and compliance.',
    bullets: [],
    image: '/infrastructure/engineering-design.png',
  },
  {
    title: 'Heavy Fabrication and Assembly',
    description: 'Fabrication and assembly of engineered steel structures up to 250 tonnes, executed in climate-controlled bays using custom jigs, fixtures, engineered lifting systems, and in-house surface preparation and coating facilities.',
    bullets: [
      'Dedicated Fabrication Bays',
      'ISO 3834-2 certified Welding capability',
      'Robotic and Defence Grade welding',
      'Certified Welding Process: SMAW, GMAW, FCAW, TIG, MIG, SAW',
      'In-house blasting & paint booth facility (Coming Soon)',
    ],
    image: '/infrastructure/robo-welding.jpg',
  },
  {
    title: 'Large Scale Precision Machining',
    description: 'Machine oversized and complex geometry components with micron-level accuracy. Advanced CNC infrastructure handles large-diameter parts and intricate multi-axis machining beyond conventional workshop capacities.',
    bullets: [
      'Diameters up to 4,000 mm',
      'Lengths up to 12,000 mm',
      '5-axis simultaneous machining',
      'Tolerance up to ±0.05 mm',
    ],
    image: '/infrastructure/swivel-elbow.jpg',
  },
  {
    title: 'Heavy Plate Processing & Beveling',
    description: 'Cut, drill, and bevel heavy steel plates up to 200 mm thickness. Automated beveling ensures weld-ready edges for critical fabrication projects.',
    bullets: [
      'Plate thickness: 8–100 mm beveling',
      'CNC profile cutting up to 200 mm',
      'Automated edge preparation',
    ],
    image: '/infrastructure/profile-cutting.jpg',
  },
  {
    title: 'Material Handling & Lifting',
    description: 'Safe handling and positioning of oversized assemblies using high-capacity overhead cranes across seven independent production bays.',
    bullets: [
      'Up to 40-tonne EOT cranes',
      '250-tonne total facility capacity',
      '80-tonne single assembly capability',
    ],
    image: '/infrastructure/fabrication-bay.png',
  },
  {
    title: 'Quality Assurance & Inspection',
    description: 'Comprehensive in-house dimensional inspection, NDT testing, and full material traceability. Every component quality checked prior to dispatch with full documentation and complete traceability.',
    bullets: [
      'CMM inspection (±0.005 mm)',
      'NDT Level II Certified Inspectors',
      'Ultrasonic, Magnetic Particle MT, Penetration and Hydrostatic Testing',
      'ISO 9001, 14001, 45001 certified',
      'MSME ZED GOLD – Sustainable Manufacturing',
    ],
    image: '/infrastructure/ISO 9001 14001 45001.png',
  },
];

/* Slideshow images – photos of the machines referenced in the HTML spec */
const slideshowImages = [
  { src: '/infrastructure/Gantry.png', caption: 'CNC Double Column Moving Gantry Milling' },
  { src: '/infrastructure/SKODA HB.jpg', caption: 'SKODA Horizontal Boring Machine' },
  { src: '/infrastructure/Union HB.jpg', caption: 'UNION Horizontal Boring Machine' },
  { src: '/infrastructure/SCHIESS HB.jpg', caption: 'SCHIESS Horizontal Boring Machine' },
  { src: '/infrastructure/BATLIBOI DRILLING.png', caption: 'BATLIBOI Drilling Machine' },
  { src: '/infrastructure/REICHLE PLANO MILLING.jpg', caption: 'REICHLE AND KNODLER Plano Milling Machine' },
  { src: '/infrastructure/LATHE.jpg', caption: 'VDF WOHLENBERG Heavy Lathe' },
  { src: '/infrastructure/Beveling Machine.jpg', caption: 'V-MAX Portable Rail Guided Beveling Machine' },
];

export const InfrastructurePage: React.FC<InfrastructurePageProps> = ({ onBack, onNavigate, embedMode }) => {
  const { isDark } = useTheme();
  const base = isDark ? 'text-white' : 'text-vmil-steel';
  const heading = isDark ? 'text-white' : 'text-vmil-blue';
  const muted = isDark ? 'text-white/80' : 'text-vmil-steel/80';
  const cardBg = isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200';
  const accent = 'text-vmil-blue';

  /* ── Slideshow state ── */
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slideshowImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slideshowImages.length) % slideshowImages.length);
  }, []);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(nextSlide, 4000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide]);

  return (
    <>
      {!embedMode && (
        <section className="relative w-full py-16 sm:py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/infrastructure/Banner Photo Spark.jpg"
              alt="VMIL Manufacturing"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-vmil-navy/85 via-vmil-navy/70 to-transparent" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              {onBack && (
                <button
                  onClick={onBack}
                  className="inline-flex items-center gap-2 text-sm font-semibold mb-6 text-white/90 hover:text-white"
                >
                  <ArrowRight className="w-4 h-4 rotate-180" /> Back
                </button>
              )}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 sm:mb-6">
                Integrated Manufacturing Infrastructure
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/80">
                From design to delivery: we fabricate, machine, test, and deliver heavy fabrications and precision-machined components from 5 to 250 tonnes.
              </p>
            </div>
          </div>
        </section>
      )}

      <div className={`w-full ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'} ${embedMode ? 'pt-6 md:pt-10' : ''}`}>
        <div className={`container mx-auto px-4 sm:px-6 lg:px-8 ${embedMode ? 'pt-6 md:pt-10 pb-12 md:pb-20' : 'py-12 sm:py-16 md:py-24'}`}>

          {/* Section: Manufacturing Excellence */}
          <section className={embedMode ? 'mb-12 md:mb-16' : 'mb-16 md:mb-24'}>
            <h2 className={`text-2xl sm:text-3xl font-bold mb-8 ${heading}`}>
              Manufacturing Excellence in Trichy, Tamil Nadu
            </h2>
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <div>
                <img
                  src="/infrastructure/manufacturing-excellence.png"
                  alt="VMIL Manufacturing Facility Aerial View"
                  className="w-full h-64 md:h-80 object-cover rounded-xl"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className={`mb-4 ${base}`}>
                  Spread across 5,430 sq.m of covered manufacturing space, our facility features seven independent production bays designed for seamless workflow and maximum efficiency.
                </p>
                <p className={`${base}`}>
                  With 40-tonne crane capacity and dedicated areas for fabrication, machining, welding, and assembly, we deliver integrated manufacturing solutions for components ranging from 5 to 250 tonnes.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {keyFacts.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div
                    key={i}
                    className={`p-4 sm:p-6 rounded-xl border ${cardBg}`}
                  >
                    <Icon className={`w-8 h-8 mb-3 ${accent}`} />
                    <div className={`font-bold text-lg ${heading}`}>{item.value}</div>
                    <div className={`text-sm ${muted}`}>{item.label}</div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section: Complete Turnkey Manufacturing — 6 Capability Cards */}
          <section className="mb-16 md:mb-24">
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${heading}`}>
              Complete Turnkey Manufacturing
            </h2>
            <p className={`mb-8 max-w-3xl ${base}`}>
              Complete in-house capabilities eliminate subcontracting delays and ensure quality control at every manufacturing stage. We deliver tested, packaged, and ready-to-install equipment to your site worldwide.
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {capabilityCards.map((card, i) => (
                <div key={i} className={`rounded-xl border overflow-hidden flex flex-col ${cardBg}`}>
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className={`font-semibold text-lg mb-2 ${heading}`}>{card.title}</h3>
                    <p className={`text-sm mb-3 ${muted}`}>{card.description}</p>
                    {card.bullets.length > 0 && (
                      <ul className="space-y-1.5 mt-auto">
                        {card.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <CheckCircle2 className={`w-4 h-4 flex-shrink-0 mt-0.5 ${accent}`} />
                            <span className={`text-sm ${base}`}>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section: Advanced Machining Infrastructure */}
          <section className={embedMode ? 'mb-12 md:mb-16' : 'mb-16 md:mb-24'}>
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${heading}`}>
              Advanced Machining Infrastructure
            </h2>
            <p className={`mb-6 max-w-3xl ${base}`}>
              Supporting Steel, Cement, Renewable, Wind, Mining, Defence and Railway sectors with large-scale precision machining.
            </p>

            {/* Machine Photos Slideshow */}
            <div
              className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden group"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Slides */}
              <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
                {slideshowImages.map((slide, i) => (
                  <div
                    key={i}
                    className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                    style={{ opacity: i === currentSlide ? 1 : 0, zIndex: i === currentSlide ? 10 : 0 }}
                  >
                    <img
                      src={slide.src}
                      alt={slide.caption}
                      className="w-full h-full object-cover"
                    />
                    {/* Caption overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-6 py-5">
                      <p className="text-white font-semibold text-lg drop-shadow-lg">{slide.caption}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Prev / Next Arrows */}
              <button
                onClick={prevSlide}
                aria-label="Previous slide"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextSlide}
                aria-label="Next slide"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Dots */}
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
                {slideshowImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentSlide(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === currentSlide
                      ? 'bg-white w-6'
                      : 'bg-white/50 hover:bg-white/80'
                      }`}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="pt-8">
            <div className={`p-6 sm:p-8 rounded-xl text-center ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`}>
              <p className={`mb-4 ${base}`}>
                Our machining infrastructure supports oversized and complex components including Boiler Components, across Steel, Wind, Cement, Renewable, Railways, Defence and Mining industries.
              </p>
              <button
                onClick={() => onNavigate('contact')}
                className="inline-flex items-center gap-2 rounded-xl bg-vmil-blue px-6 py-3 text-white font-semibold hover:bg-vmil-blue/90 transition-colors"
              >
                Discuss Your Core Machining Requirements <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};
